import { planLabel } from 'utils/learningHubUi';

export const NOT_AVAILABLE = 'Not available';
export const PENDING_MESSAGE = 'A Learning Hub access request is already pending';
export const APPROVED_MESSAGE = 'You already have Learning Hub access';
export const INVALID_COURSES_MESSAGE = 'One or more selected courses are not available in Learning Hub';

const hasValue = (value) => value != null && value !== '';

export const formatPlan = (plan) => planLabel(plan) || NOT_AVAILABLE;

export const formatAccessState = (value) => {
  if (!hasValue(value)) {
    return NOT_AVAILABLE;
  }
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const formatAccessDate = (value) => {
  if (!hasValue(value)) {
    return NOT_AVAILABLE;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) {
    return String(value);
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const formatDaysRemaining = (value) => (
  hasValue(value) ? String(value) : NOT_AVAILABLE
);

export const currentPlanFields = (access) => ([
  { label: 'Plan', value: formatPlan(access?.plan) },
  { label: 'Status', value: formatAccessState(access?.access_state) },
  { label: 'Start date', value: formatAccessDate(access?.access_start_date) },
  { label: 'End date', value: formatAccessDate(access?.access_end_date) },
  { label: 'Days remaining', value: formatDaysRemaining(access?.access_days_remaining) },
]);

export const upgradeSubmitLabel = (selectedCount) => (
  selectedCount > 0 ? 'Request Learning Hub + Selected Courses' : 'Request Learning Hub'
);

export const selectedCountLabel = (selectedCount) => {
  if (!selectedCount) {
    return 'No courses selected';
  }
  return selectedCount === 1 ? '1 course selected' : `${selectedCount} courses selected`;
};

export const courseName = (course) => (
  hasValue(course?.name) ? String(course.name) : NOT_AVAILABLE
);

export const courseMeta = (course) => {
  const category = course?.category;
  const categoryText = typeof category === 'string'
    ? category
    : (category?.name || category?.category || '');
  const provider = hasValue(course?.provider) ? String(course.provider) : '';
  return [categoryText, provider].filter(Boolean).join(' · ');
};

export const buildUpgradePayload = (courseIds, comment) => {
  const payload = {
    requested_plan: 'learning_hub',
    selected_courses: Array.isArray(courseIds) ? courseIds : [],
  };
  const trimmed = String(comment || '').trim();
  if (trimmed) {
    payload.comment = trimmed;
  }
  return payload;
};

export const classifyUpgradeResponse = (response) => {
  const httpStatus = Number(response?.httpStatus);
  const message = String(response?.message || '');
  if (httpStatus === 409) {
    if (message === APPROVED_MESSAGE || /already have Learning Hub access/i.test(message)) {
      return 'approved';
    }
    if (message === PENDING_MESSAGE || /already pending/i.test(message)) {
      return 'already_pending';
    }
    return 'error';
  }
  if (httpStatus === 400) {
    if (message === INVALID_COURSES_MESSAGE || /not available in Learning Hub/i.test(message)) {
      return 'invalid_courses';
    }
    return 'error';
  }
  const succeeded = httpStatus === 200 || response?.status === true;
  if (
    succeeded
    && response?.data?.status === 'pending'
    && response?.data?.requested_plan === 'learning_hub'
  ) {
    return 'pending';
  }
  return 'error';
};
